import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import {
  MapPin, ArrowLeft, Camera, Sparkle, TreeEvergreen, Drop,
  Mountains, Plant, Leaf,
} from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";

const SUBJECT_ICON = {
  tree: TreeEvergreen, forest: TreeEvergreen,
  river: Drop, stream: Drop, lake: Drop, pond: Drop, wetland: Drop,
  beach: Drop, shore: Drop, coast: Drop,
  garden: Plant, farm: Plant, field: Plant, backyard: Plant, park: Plant,
  mountain: Mountains, hill: Mountains, glacier: Mountains, meadow: Leaf,
  other: Sparkle,
};

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

function TimelineEntry({ o }) {
  const cover = o.media?.[0]?.url;
  const when = (o.observed_at || o.created_at || "").slice(0, 10);
  return (
    <div className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-4 md:gap-6" data-testid={`timeline-${o.id}`}>
      <div className="pt-1 text-right">
        <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{when}</div>
      </div>
      <div className="border-l-2 border-[#E4A15C]/40 pl-5 pb-6 relative">
        <div className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-[color:var(--np-brand,#B85F2A)]" />
        <div className="font-serif-display text-xl md:text-2xl leading-tight">{o.title}</div>
        {o.body && <p className="mt-2 text-[15px] leading-relaxed text-[#241209]">{o.body}</p>}
        {cover && (
          <div className="mt-3 rounded-xl overflow-hidden border border-black/10 max-w-2xl">
            <img src={abs(cover)} alt={o.title} className="w-full h-auto" loading="lazy" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdoptedPlaceDetail() {
  const { id } = useParams();
  const [place, setPlace] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/adopt-a-place/${id}`)
      .then(({ data }) => {
        setPlace(data?.place || null);
        setTimeline(data?.timeline || []);
      })
      .catch(() => { setPlace(null); setTimeline([]); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const submitUpdate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() && !form.body.trim()) return;
    setSaving(true);
    try {
      await http.post(`/adopt-a-place/${id}/observations`, {
        title: form.title.trim() || undefined,
        body: form.body.trim() || undefined,
      });
      setForm({ title: "", body: "" });
      setShowForm(false);
      load();
    } finally { setSaving(false); }
  };

  if (loading) {
    return <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 px-5 md:px-10">
      <div className="max-w-[1000px] mx-auto animate-pulse space-y-4">
        <div className="h-6 w-1/4 bg-white/70 rounded" />
        <div className="h-14 w-3/4 bg-white/70 rounded" />
        <div className="h-40 bg-white/70 rounded-2xl" />
      </div>
    </main>;
  }
  if (!place) {
    return <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 px-5 md:px-10">
      <div className="max-w-[1000px] mx-auto">
        <div className="font-serif-display text-3xl">Adopted place not found.</div>
        <Link to="/adopt-a-place" className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <ArrowLeft size={12} weight="bold" /> Back to adopted places
        </Link>
      </div>
    </main>;
  }

  const Icon = SUBJECT_ICON[place.subject_type] || Sparkle;
  const loc = [place.location?.city, place.location?.state, place.location?.country].filter(Boolean).join(", ");

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="adopt-a-place-detail">
      <section className="max-w-[1000px] mx-auto px-5 md:px-10">
        <Link to="/adopt-a-place" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:gap-2 transition-all">
          <ArrowLeft size={12} weight="bold" /> All adopted places
        </Link>
        <div className="mt-4 flex items-start gap-4">
          <Icon size={40} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] shrink-0 mt-2" />
          <div className="flex-1">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / {place.subject_type} · rephotographed {place.schedule}
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight mt-1">
              {place.name}
            </h1>
            {loc && (
              <div className="mt-2 text-sm text-[#3A3830] flex items-center gap-1">
                <MapPin size={14} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> {loc}
              </div>
            )}
          </div>
          <ShareButton
            url={`${window.location.origin}/adopt-a-place/${place.id}`}
            title={place.name}
            text={`Adopted place on NaturePulse — ${place.subject_type}${loc ? " · " + loc : ""}`}
            variant="pill"
            testid="adoption-share"
          />
        </div>
        {place.description && (
          <p className="mt-6 font-serif-display text-lg leading-relaxed text-[#241209] max-w-3xl">
            {place.description}
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => setShowForm((v) => !v)}
            data-testid="adopt-add-update"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Camera size={14} weight="bold" /> Add an update
          </button>
        </div>

        {showForm && (
          <form onSubmit={submitUpdate} className="mt-4 rounded-2xl border border-black/10 bg-white p-5" data-testid="adopt-update-form">
            <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Title</label>
            <input
              type="text" required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={`e.g. ${new Date().toLocaleString("en-US", { month: "long", year: "numeric" })} — full canopy…`}
              data-testid="adopt-update-title"
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-[15px]"
            />
            <label className="mt-3 block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Notes (optional)</label>
            <textarea
              rows={4}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="What did you notice today?"
              data-testid="adopt-update-body"
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-[15px]"
            />
            <div className="mt-3 flex items-center gap-3">
              <button
                type="submit" disabled={saving}
                data-testid="adopt-update-submit"
                className="rounded-full bg-[#241209] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[11px] tracking-[0.28em] uppercase disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save update"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70 hover:text-[#241209]">
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="max-w-[1000px] mx-auto px-5 md:px-10 mt-14">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
          / Timeline · {timeline.length} update{timeline.length === 1 ? "" : "s"}
        </div>
        {timeline.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
            <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">No observations yet.</div>
            <div className="mt-1 text-sm text-[#3A3830]">Hit "Add an update" to begin the record.</div>
          </div>
        ) : (
          <div className="space-y-1" data-testid="adopt-timeline">
            {timeline.map((o) => <TimelineEntry key={o.id} o={o} />)}
          </div>
        )}
      </section>
    </main>
  );
}
